import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';

import { usersv2Client } from '@/adapters/schemaClients';
import { Pylon } from '@/lib/Pylon';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';

export const PYLON_PENDING_MESSAGE_POLL_INTERVAL_MS = 30_000;

const isPylonChatOpenAtom = atom(false);

const fetchPendingMessageId = async () => {
  const { data } = await usersv2Client.getCurrentUserPylonPendingMessage();
  return data.message_id || null;
};

const acknowledgePendingMessage = async () => {
  const messageId = await fetchPendingMessageId();
  if (messageId) {
    await usersv2Client.acknowledgeCurrentUserPylonPendingMessage({
      acknowledgeCurrentUserPylonPendingMessageRequest: {
        message_id: messageId
      }
    });
  }
};

export const usePylonPendingMessagePopup = () => {
  const setIsPylonChatOpen = useSetAtom(isPylonChatOpenAtom);
  const { data: pendingMessageId } = useQuery(
    ['pylonPendingMessage'],
    fetchPendingMessageId,
    {
      enabled: FeatureFlagService.isEnabled(ExacareFeature.PYLON),
      refetchInterval: PYLON_PENDING_MESSAGE_POLL_INTERVAL_MS
    }
  );

  useEffect(() => {
    if (pendingMessageId) {
      Pylon.show();
    }
  }, [pendingMessageId]);

  useEffect(() => {
    window.Pylon?.('onShow', () => setIsPylonChatOpen(true));
    window.Pylon?.('onHide', () => {
      setIsPylonChatOpen(false);
      acknowledgePendingMessage().catch((error) => {
        console.error('Pylon pending message acknowledgement failed:', error);
      });
    });

    return () => {
      window.Pylon?.('onShow', null);
      window.Pylon?.('onHide', null);
    };
  }, [setIsPylonChatOpen]);
};

export const usePylonChat = () => {
  const isPylonChatOpen = useAtomValue(isPylonChatOpenAtom);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  useEffect(() => {
    window.Pylon?.('onChangeUnreadMessagesCount', (count: number) => {
      setUnreadMessageCount(count);
    });

    return () => {
      window.Pylon?.('onChangeUnreadMessagesCount', null);
    };
  }, []);

  useEffect(() => {
    if (!isPylonChatOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const pylonChat = document.getElementById('pylon-chat');
      if (pylonChat && !pylonChat.contains(event.target as Node)) {
        Pylon.hide();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isPylonChatOpen]);

  const toggleChat = useCallback(() => {
    if (isPylonChatOpen) {
      Pylon.hide();
    } else {
      Pylon.show();
    }
  }, [isPylonChatOpen]);

  return {
    unreadMessageCount,
    toggleChat
  };
};
